import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS } from 'constants/featureFlags';
import Modal from 'components/shared/DesignSystem/Modal';
import { Toggle as Switch } from 'components/core/forms';
import ToggleGroup from 'components/shared/DesignSystem/ToggleGroup';
import Toggle from 'components/shared/DesignSystem/Toggle';
import styled from 'styled-components';
import { Button } from '@companycam/slab-web';

const StyledModal = styled(Modal)`
  width: 553px;
  display: flex;
  flex-direction: column;
`;

const switchRowStyle = {
  justifyContent: 'space-between',
  marginBottom: 16,
};

const toggleTitleStyle = {
  color: '#4d5a68',
  fontSize: '14px',
  fontWeight: 400,
};

const toggleRowStyle = {
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
};

const messages = defineMessages({
  exportDescription: {
    id: 'Projects.taskList.exportPdf.description',
    defaultMessage:
      'This PDF export will be a snapshot of this list in its current state.',
    description: 'Description for export tasklist pdf modal',
  },
  includePhotosToggle: {
    id: 'Projects.taskList.exportPdf.includePhotosToggle',
    defaultMessage: 'Include Photos',
    description: 'Toggle label for including photos',
  },
  includePhotoDescriptionToggle: {
    id: 'Projects.taskList.exportPdf.includePhotoDescriptionToggle',
    defaultMessage: 'Include Photo Capture Details',
    description: 'Toggle label for including photo description',
  },
  saveToProjectDocuments: {
    id: 'Projects.taskList.exportPdf.saveToProjectDocumentsToggle',
    defaultMessage: 'Save to Project Documents',
    description: 'Toggle label for saving to project documents',
  },
  photosPerRowToggle: {
    id: 'Projects.taskList.exportPdf.photosPerRowToggle',
    defaultMessage: 'Photos per Row',
    description: 'Toggle label for photos per row',
  },
  export: {
    id: 'Projects.taskList.exportPdf.exportBtn',
    defaultMessage: 'Export',
    description: 'Label for export button',
  },
  includeSkippedSections: {
    id: 'Projects.taskList.exportPdf.includeSkippedSectionsToggle',
    defaultMessage: 'Include Sections Marked N/A in Export',
    description: 'Toggle label for show skipped sections',
  },
});

const ExportModal = ({
  isOpen,
  onRequestClose,
  onExportClick,
  toggleDispatch,
  toggleState,
  loading,
}) => {
  const { formatMessage } = useIntl();
  const flags = useFlags();
  const skipSectionsEnabled = flags[LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS];

  const {
    includePhotos,
    includePhotoDetails,
    photosPerRow,
    saveToProjectDocuments,
    includeSkippedSections,
  } = toggleState;

  return (
    <StyledModal
      isOpen={isOpen}
      isClosable
      onRequestClose={onRequestClose}
      title={formatMessage({
        id: 'Projects.taskList.taskListHeader.exportToPdf',
      })}
      className="modal-content-wrap"
    >
      <p className="instructions">
        {formatMessage(messages.exportDescription)}
      </p>

      <Switch
        key="includePhotos"
        label={formatMessage(messages.includePhotosToggle)}
        name="includePhotos"
        checked={includePhotos}
        onChange={() => toggleDispatch({ type: 'includePhotos' })}
        style={switchRowStyle}
        labelOff={formatMessage({ id: 'Common.toggleOff' })}
        labelOn={formatMessage({ id: 'Common.toggleOn' })}
        disabled={loading}
      />

      <Switch
        key="includePhotoDescription"
        label={formatMessage(messages.includePhotoDescriptionToggle)}
        name="includePhotoDescription"
        checked={includePhotoDetails}
        onChange={() => toggleDispatch({ type: 'includePhotoDetails' })}
        style={switchRowStyle}
        labelOff={formatMessage({ id: 'Common.toggleOff' })}
        labelOn={formatMessage({ id: 'Common.toggleOn' })}
        disabled={loading || !includePhotos}
      />

      {skipSectionsEnabled ? (
        <Switch
          key="includeSkippedSections"
          label={formatMessage(messages.includeSkippedSections)}
          name="includeSkippedSections"
          checked={includeSkippedSections}
          onChange={() => toggleDispatch({ type: 'includeSkippedSections' })}
          style={switchRowStyle}
          labelOff={formatMessage({ id: 'Common.toggleOff' })}
          labelOn={formatMessage({ id: 'Common.toggleOn' })}
          disabled={loading}
        />
      ) : null}

      <Switch
        key="saveToProjectDocuments"
        label={formatMessage(messages.saveToProjectDocuments)}
        name="saveToProjectDocuments"
        checked={saveToProjectDocuments}
        onChange={() => toggleDispatch({ type: 'saveToProjectDocuments' })}
        style={switchRowStyle}
        labelOff={formatMessage({ id: 'Common.toggleOff' })}
        labelOn={formatMessage({ id: 'Common.toggleOn' })}
        disabled={loading}
      />

      <ToggleGroup
        title={formatMessage(messages.photosPerRowToggle)}
        name="photosPerRow"
        onChange={() => toggleDispatch({ type: 'photosPerRow' })}
        defaultValue={photosPerRow}
        titleStyle={toggleTitleStyle}
        style={toggleRowStyle}
        disabled={loading || !includePhotos}
      >
        <Toggle label="2" value={2} />
        <Toggle label="3" value={3} />
      </ToggleGroup>

      <div className="btn-container">
        <Button color="primary" onClick={onExportClick} loading={loading}>
          {formatMessage(messages.export)}
        </Button>
      </div>
    </StyledModal>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onExportClick: PropTypes.func,
  toggleState: PropTypes.object,
  toggleDispatch: PropTypes.func,
  loading: PropTypes.bool,
};

export default ExportModal;
